// Constants the map CHROME owns — the header, its popovers, the notice stack,
// the cursor tooltip, the plate ribbon and the keys. Kept out of `map_config.ts`
// on purpose: that file is the map's own vocabulary (paint, zoom thresholds,
// fetch timings) and is imported by the code that draws, while nothing here is
// read by anything but the shell and the panels.
import {
  COUNTRY_EDGE_OPACITY,
  COUNTRY_FILL_OPACITY,
  COVERAGE_DRAWN,
  COVERAGE_NAMED,
  PLAIN_BACKGROUND_COLOR,
  REGION_LINE_COLOR,
  REGION_RIBBON,
  REGION_RIBBON_LAYER_OPACITY,
  SELECTED_CASING_COLOR,
  SELECTED_FILL_COLOR,
  SELECTED_LINE_COLOR,
  UNCOVERED_COUNTRY_LINE_COLOR,
  VINEYARD_COLOR,
  VINEYARD_EDGE_COLOR,
  VINEYARD_MIN_ZOOM,
} from './map_config'

// Where a signed-out reader goes to get a journal. The atlas is public; saving
// a tasting or a wish is what needs an account, so the chrome offers this in
// the place a signed-in reader finds Journal and Wishlist. From an open panel
// the link adds `?return_to=` with that panel's path, which the server honours
// for a path on its own host, so signing in from a place returns to the place.
export const SIGN_IN_PATH = '/session/new'

// The ids the two disclosure buttons point at with `aria-controls`. Literals in
// one place because each is written twice, on the button and on the surface it
// opens, and a pair that drifted apart would still render and still be wrong.
export const LAYERS_PANEL_ID = 'layers-panel'
export const FILTERS_PANEL_ID = 'winery-filters-panel'

// Which of the header's two popovers is open. One value rather than two
// booleans because they are mutually exclusive by design: on a phone each is a
// bottom sheet, and two sheets stacked on one another is a state with no
// sensible reading.
export type ChromePopover = 'layers' | 'filters'

// How far the tooltip sits from the pointer, in CSS pixels. ABOVE it by the
// first, so the hand and the cursor are not over what they are asking about
// and the thing pointed at stays in view under the pointer; BELOW it by the
// second — the height of an arrow cursor and a little — when above would put
// it off the screen or on top of the name the map has already drawn.
export const HOVER_LABEL_OFFSET_ABOVE = 12
export const HOVER_LABEL_OFFSET_BELOW = 22

// The focus ring every button and link in the chrome wears, in the accent. Only
// on `:focus-visible`, so a pointer press does not leave a ring behind and a
// keyboard always gets one — over a map, focus with no outline is focus lost.
export const CHROME_FOCUS_CLASS =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'

// The skin shared by the header's floating controls, as class strings so the
// two disclosure buttons cannot drift apart. `aria-[expanded=false]:` guards
// the hover colour: an open button is filled with the accent and carries white
// text, and a bare `hover:bg-white` would wipe the label out under the pointer.
export const CHROME_BUTTON_CLASS =
  'pointer-events-auto inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-full bg-surface-raised ' +
  'text-sm font-medium text-ink shadow-chrome ring-1 ring-black/5 transition-colors duration-100 ' +
  'aria-[expanded=false]:hover:bg-white aria-expanded:bg-accent aria-expanded:text-white ' +
  `${CHROME_FOCUS_CLASS} md:h-10 md:px-3.5`

// The word on one of those buttons: its name at every width, and DRAWN only
// where the bar has room for it. Under that the icon carries the button and
// the row keeps its width for the search field.
//
// "Room" is not the viewport alone. With no panel open the bar has the window
// and the words fit from `md`. BESIDE AN OPEN PANEL it has 408px less, and
// three labelled controls in what is left wrapped the header into three ragged
// rows at 768px and sent the nav to a row of its own at 1024px; there the words
// wait until `xl`. `beside` is the shell saying which it is. (A container query
// would say it without being told — and cannot be used here: a size container
// is layout containment, which would make the bar the containing block of the
// `position: fixed` sheets that live inside it. See the header in
// atlas_layout.tsx.)
export const chromeButtonLabelClass = (beside: boolean) =>
  beside ? 'sr-only xl:not-sr-only' : 'sr-only md:not-sr-only'

// And the width of a button that is only its icon until then.
export const chromeIconButtonClass = (beside: boolean) => (beside ? 'w-11 xl:w-auto' : 'w-11 md:w-auto')

// ONE HEAD AND ONE WAY OUT FOR EVERY SHEET FROM THE BOTTOM EDGE. On a phone the
// Layers list, the filters and the detail panel all rise from the same edge,
// and they used to behave three ways: one had a title and no way out but the
// button at the top of the screen that had opened it, one had a Done and no
// title. Each now opens with what it is called, in the display face, and each
// can be put away from inside it, at the thumb.
export const CHROME_SHEET_TITLE_CLASS = 'font-display text-[19px] font-semibold tracking-tight text-ink'
export const CHROME_SHEET_DONE_CLASS =
  `inline-flex min-h-11 items-center rounded-full bg-accent px-5 text-sm font-medium text-white ${CHROME_FOCUS_CLASS}`

// A popover from `md` up, a bottom sheet below it. `fixed` on a phone so the
// sheet is positioned by the viewport while staying DOM-adjacent to the button
// that opens it (tab order and `aria-controls` stay natural); the header it
// lives in must therefore never gain a `transform`, `filter`, `contain` or
// `will-change`, any of which would make the header the sheet's containing
// block and clip it to the header's own box.
//
// Two numbers from `md` up are measured against the narrowest case, 768px with
// a detail panel open, where the header has 344px and wraps to three rows:
// search, then the two buttons, then the nav.
//
// - `md:w-[22rem]` for BOTH surfaces. There the popover opens over the nav's
//   row, and at 352px it covers the nav whole; at 320px the last link stuck
//   out from behind its right edge.
// - `md:max-h-[calc(100dvh-8rem)]`. The popover hangs under the second row
//   there (112px down), so anything taller ran its footer off the bottom of
//   the screen. On a single-row header it simply stops 64px short of the edge.
export const CHROME_POPOVER_CLASS =
  'pointer-events-auto fixed inset-x-0 bottom-0 z-10 max-h-[70dvh] rounded-t-2xl bg-surface-raised text-ink ' +
  'shadow-panel ring-1 ring-black/5 motion-safe:animate-panel-in-y md:absolute md:inset-x-auto md:bottom-auto ' +
  'md:left-0 md:top-full md:mt-2 md:max-h-[calc(100dvh-8rem)] md:w-[22rem] md:rounded-2xl ' +
  'md:motion-safe:animate-chrome-in'

// The journal's accent on a native control. `@tailwindcss/forms` otherwise
// supplies a grey border and a blue check that belong to no surface of this app.
//
// Two things about it are contrast, not taste. The box's own edge is the soft
// ink: at 20% black it was 1.6 to 1 against the paper, and an unticked option
// that cannot be seen as a control is not one. And focus is the same solid
// 2px claret outline every other control in the chrome wears
// (`CHROME_FOCUS_CLASS`) — it was the forms plugin's ring at 40% of the accent,
// about 2.2 to 1, the one pale ring in a row of solid ones. The plugin's ring
// is switched off so the two are not drawn together.
const CHROME_CONTROL_CLASS =
  'h-4 w-4 shrink-0 border-ink-soft text-accent focus:ring-0 focus:ring-offset-0 ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-40'
export const CHROME_CHECKBOX_CLASS = `${CHROME_CONTROL_CLASS} rounded`
export const CHROME_RADIO_CLASS = `${CHROME_CONTROL_CLASS} rounded-full`

// THE PLATE, AS THE CHROME DRAWS IT.
//
// The plate ribbon, the key in the Layers popover and the mark in a panel's
// title bar are all small drawings of what the map draws, and a key that
// documents a different mark from the one on the map is worse than no key. So
// EVERY PAINT VALUE THEY USE IS THE MAP'S OWN CONSTANT, re-exported here under
// the name the keys use. One file says which map constant each swatch stands
// for, and none of them can drift: retune a ribbon tint or a country's
// strength in `map_config.ts` and the key is retuned with it.
//
// (It was not always so. The keys were drawn before the map they describe was
// redrawn, and for a while the newer values — the ribbon tints, the country
// strengths, the vineyard — were literals here, copied from the direction's
// notes. Two had already drifted by the time the two met: the dashed line's
// strength, 0.8 here against the 0.85 the map draws, and the bare-ground
// edge, a shade off.)
//
// The only numbers of the chrome's own are the ones with no counterpart on
// the map: how wide a ribbon is IN A SWATCH, and how long the scale bar may be.
//
// `lib/atlas_style.ts` is deliberately NOT a source for any of them. It is in
// the map's lazy chunk, and importing the vineyard green from it would pull the
// whole basemap style into the entry bundle to colour one 20px swatch. That is
// why the style reads the vineyard from `map_config.ts` rather than the other
// way round.
export const PLATE_LAND_COLOR = PLAIN_BACKGROUND_COLOR
export const PLATE_CLARET = REGION_LINE_COLOR
export const PLATE_SELECTED_RIBBON_COLOR = SELECTED_FILL_COLOR
export const PLATE_SELECTED_LINE_COLOR = SELECTED_LINE_COLOR
export const PLATE_PAPER_COLOR = SELECTED_CASING_COLOR

// DEPTH IS ONE HUE, NARROWER AND STRONGER. An appellation puts no film on the
// ground any more: it is a claret hairline and a ribbon inside it, and each
// level of nesting draws a narrower ribbon in a deeper tint. (It was the
// darkness of stacked translucent fills, P4's KTD13, which coloured the whole
// page at appellation zoom.) The tints are what the ribbon is printed in BEFORE
// the layer is laid on the map at `PLATE_RIBBON_LAYER_OPACITY`; over chalk they
// land at about 11%, 24% and 38% claret. A swatch draws them the same way —
// the tint, at the layer's opacity, over the land colour — so it shows the mark
// as it reaches the reader rather than as it is specified.
//
// One entry per depth the map draws (`REGION_RIBBON`): three, because the data
// has three tiers, and anything nested deeper draws as the third.
export const PLATE_RIBBON_TINTS: readonly string[] = REGION_RIBBON.map((depth) => depth.tint)
export const PLATE_RIBBON_LAYER_OPACITY = REGION_RIBBON_LAYER_OPACITY

// The ribbon's width IN A SWATCH, by depth. The map's own widths are zoom ramps
// (8.9, 5.7 and 2.9px at the Napa view); these are that step at the size of a
// 20x14 patch, where the proportion is what has to survive.
export const PLATE_KEY_RIBBON_WIDTHS = [4.5, 3, 1.5] as const

// A country, by what the atlas holds for it. Drawn: a claret tint and a solid
// ribbon inside the coast. Imported without boundaries: a fainter tint and a
// DASHED claret line — the dash, not the tint, is what separates it from
// shaded relief, which is umber and has no edge. Nothing yet: bare ground, so
// its swatch is the land colour with a border that makes a patch of it
// findable in a key. (The dash itself is the browser's: a CSS border cannot be
// given the map's `NAMED_COUNTRY_LINE_DASH`, and at 20px "dashed" is the claim.)
export const PLATE_COUNTRY_TINT_OPACITY = {
  drawn: COUNTRY_FILL_OPACITY[COVERAGE_DRAWN],
  named: COUNTRY_FILL_OPACITY[COVERAGE_NAMED],
} as const
export const PLATE_COUNTRY_RIBBON_OPACITY = COUNTRY_EDGE_OPACITY[COVERAGE_DRAWN]
export const PLATE_COUNTRY_DASH_OPACITY = COUNTRY_EDGE_OPACITY[COVERAGE_NAMED]
export const PLATE_BARE_GROUND_EDGE_COLOR = UNCOVERED_COUNTRY_LINE_COLOR

// The vines themselves, which the basemap draws from zoom 8: the one saturated
// ground colour on the plate, and so the one piece of ground the keys name.
export const PLATE_VINEYARD_COLOR = VINEYARD_COLOR
export const PLATE_VINEYARD_EDGE_COLOR = VINEYARD_EDGE_COLOR
export const PLATE_VINEYARD_MIN_ZOOM = VINEYARD_MIN_ZOOM

// The longest the ribbon's scale bar is drawn, in CSS pixels. The bar is the
// largest round distance (1, 2 or 5 times a power of ten) that fits in this,
// so it is between 40% and 100% of it. Kept short on purpose: the strip shares
// a row with the attribution, and every pixel of bar is a pixel of key.
export const PLATE_SCALE_MAX_PX = 64

// The same policies as they follow a count, on a place's panel: "39 walk-in ·
// 6 by appointment". Lower case, because each is the tail of a phrase.
export const WINERY_VISITING_COUNT_LABELS: Record<string, string> = {
  walk_in: 'walk-in',
  appointment: 'by appointment',
  // "for members only", so that it follows a count: "1 members only" was not
  // a phrase.
  members_only: 'for members only',
  invite_only: 'by invitation',
  not_open: 'not open',
}

// What a dot in the neutral colour means, where the chrome says it short: the
// plate ribbon and a winery's own Size row. No source states a figure and
// nothing the atlas holds lets it estimate one.
export const WINERY_SIZE_NOT_STATED_LABEL = 'Not stated'
